/**
 * Shared anti-spam field names and a pure bot heuristic. Safe to import from
 * both client components (the forms) and server route handlers — it has no
 * Node-only dependencies.
 *
 * Two cheap, low-false-positive signals:
 *  - Honeypot: a visually hidden field a human never sees. If it arrives with
 *    a value, a bot filled it in.
 *  - Time trap: forms stamp how long the visitor took to fill them in. A submit
 *    that lands in well under a second is almost always automated.
 */

/** Hidden honeypot input name. A real visitor leaves it empty. */
export const HONEYPOT_FIELD = "company_website";

/** Hidden field carrying how many milliseconds the form was on screen. */
export const ELAPSED_FIELD = "form_elapsed_ms";

/** Submissions faster than this are treated as automated. */
export const MIN_ELAPSED_MS = 1200;

/**
 * Returns true when a submission looks automated. Lenient by design: a missing
 * or unparseable elapsed value is allowed through (native no-JS posts, cached
 * pages), so only a *present and implausibly small* time trips it.
 */
export function looksLikeBot(data: Record<string, unknown>): boolean {
  const honeypot = data[HONEYPOT_FIELD];
  if (typeof honeypot === "string" && honeypot.trim() !== "") return true;

  const raw = data[ELAPSED_FIELD];
  const elapsed = typeof raw === "string" || typeof raw === "number" ? Number(raw) : NaN;
  if (Number.isFinite(elapsed) && elapsed >= 0 && elapsed < MIN_ELAPSED_MS) {
    return true;
  }

  return false;
}
